import {
  NestedComponentStyleOverrides,
  StyleBinding,
  StyleClassDefinition,
  StyleClassDefinitions,
  ThemeVariables,
} from '@sapappgyver/appgyver-descriptors/lib/dolan/v4';
import { constant, isEmpty, mapValues, set, unset } from 'lodash/fp';
import { convertBindingToStylePropertyFormat, getBindingKey, getBindingSource } from '../../modules/bindings';
import {
  designSystemDefaultCompositeComponentLayoutProperties,
  designSystemStyleClassesByComponentName,
} from '../../modules/design-system';
import mergeNestedComponentStyleOverrides from './utils/mergeNestedComponentStyleOverrides';
import ComponentInstanceProxy from '../dolanruntime/ComponentInstanceProxy';

function convertNestedOverridesToStylePropertyFormat(nestedOverrides: NestedComponentStyleOverrides) {
  return mapValues((value) => {
    if (value?.style) {
      return {
        ...value,
        style: mapValues(convertBindingToStylePropertyFormat, value?.style),
      };
    }
    return value;
  }, nestedOverrides);
}

export function pushStyleClassOverride(
  styleClasses: Record<string, StyleClassDefinitions>,
  componentName: string,
  sourceStyleClass: string,
  styleClassOverride: StyleClassDefinition,
) {
  const componentStyleClasses = componentName ? styleClasses?.[componentName] : undefined;
  const styleClassDef = sourceStyleClass ? componentStyleClasses?.[sourceStyleClass] : null;
  // Merge the provided nested overrides on top of the existing ones
  const mergedNestedOverrides = convertNestedOverridesToStylePropertyFormat(
    mergeNestedComponentStyleOverrides(styleClassDef?.nested_overrides, styleClassOverride?.nested_overrides),
  );
  const hasNestedOverrides = !isEmpty(mergedNestedOverrides);
  const mergedStyle = mapValues(convertBindingToStylePropertyFormat, {
    ...styleClassDef?.style,
    ...styleClassOverride.style,
  });
  const result: StyleClassDefinition = {
    ...styleClassDef,
    ...styleClassOverride,
  };
  // Include nested_overrides unless empty (for composites)
  if (hasNestedOverrides) {
    result.nested_overrides = mergedNestedOverrides;
  }
  // If nested overrides is non-empty then empty styles can be excluded
  // (composites do not need it)
  if (!hasNestedOverrides || !isEmpty(mergedStyle)) {
    result.style = mergedStyle;
  }
  return result;
}

export function updateStyleClass(
  styleClasses: Record<string, Record<string, StyleClassDefinition>>,
  componentName: string,
  styleClassName: string,
  newStyleClass: StyleClassDefinition,
) {
  return set([componentName, styleClassName], newStyleClass, styleClasses);
}

export function deleteStyleClass(
  styleClasses: Record<string, Record<string, StyleClassDefinition>>,
  componentName: string,
  styleClass: string,
) {
  return unset([componentName, styleClass], styleClasses);
}

export function getComponentStyleClassTitle(
  componentName: string,
  componentStyleClass: string,
  dolanStyleClasses: Record<string, Record<string, StyleClassDefinition>> = {},
) {
  const componentStyleClasses = dolanStyleClasses[componentName] || {};
  const styleClasses =
    componentName && componentName in designSystemStyleClassesByComponentName
      ? designSystemStyleClassesByComponentName[componentName]
      : [];
  const matchingStyleClass =
    styleClasses?.find((styleClass) => styleClass.id === componentStyleClass) ||
    componentStyleClasses[componentStyleClass];
  return matchingStyleClass?.title || componentStyleClass;
}

export function getComponentDefaultStyleClass(
  componentName?: string,
  componentStyleClasses: Record<string, StyleClassDefinition> = {},
) {
  const defaultStyleClassOptions =
    componentName && componentName in designSystemStyleClassesByComponentName
      ? designSystemStyleClassesByComponentName[componentName]
      : [];
  if (defaultStyleClassOptions?.length) {
    return defaultStyleClassOptions[0].id;
  }
  if (componentStyleClasses.default) {
    return 'default';
  }
  const styleClassIds = Object.keys(componentStyleClasses);
  return styleClassIds[0] ?? undefined;
}

export function getComponentDefaultLayoutProperties(component?: ComponentInstanceProxy) {
  const isComposite = !!component?.description?.composite_json;
  return isComposite ? designSystemDefaultCompositeComponentLayoutProperties : {};
}

export function mapObjectProperties<T, O extends Record<string, T>>(
  properties: (keyof O)[],
  mapper: (prop: keyof O, value: O[keyof O], obj: O) => O[keyof O] | undefined,
  object: O,
): O {
  return properties.reduce((obj, prop) => {
    const value = mapper(prop, obj[prop], obj);
    return value !== undefined ? set(prop, value, obj) : unset(prop, obj);
  }, object);
}

export function setOrUnsetObjectProperies<T, O extends Record<string, T>>(
  properties: (keyof O)[],
  value: O[keyof O] | undefined,
  object: O,
): O {
  return mapObjectProperties(properties, constant(value), object);
}

export const getBindingTitle = (binding: StyleBinding | null | undefined, themeVariables: ThemeVariables) =>
  getBindingSource(binding) === 'theme'
    ? themeVariables[getBindingKey(binding) as string]?.title || (getBindingKey(binding) as string)
    : undefined;

export const toNumber = (input: any): number => {
  if (typeof input === 'number') return input;
  if (typeof input !== 'string') return NaN;
  if (!input.length) return NaN;
  return Number(input);
};

export const formatBindingValue = (value: string | number): string =>
  !Number.isNaN(toNumber(value)) ? `${value}px` : (value as string);
